import { useEffect, useState, type ChangeEvent } from 'react';
import {
  InlineNotification,
  Modal,
  RadioButton,
  RadioButtonGroup,
  TextInput,
} from '@carbon/react';
import { useEval3Page } from './context';
import { useCreateWorkspace } from './hooks';
import styles from './add-workspace-modal.module.scss';

/** The two things the Add modal can create (§11g). `null` = nothing chosen yet. */
type AddChoice = 'eval' | 'workspace' | null;

interface AddWorkspaceModalProps {
  /** Whether the modal is shown. */
  open: boolean;
  /** Close without creating (Cancel / ESC / ✕). */
  onClose: () => void;
  /** Called with the new workspace's id after a successful create. */
  onCreated: (workspaceId: number) => void;
  /**
   * The workspace a from-scratch draft eval would attach to (the active folder, else
   * the most-recently-active one). `undefined` when the package has no workspaces — the
   * "Add an eval" option is then disabled, leaving only "Create a new workspace".
   */
  addEvalTarget?: { id: number; name: string };
  /** Start a client-only from-scratch draft eval under `workspaceId` (set draft + select it). */
  onAddEval: (workspaceId: number) => void;
}

/**
 * The Add modal (mock §11g): a two-option `RadioButtonGroup` — "Add an eval to
 * [workspace]" (a client-only from-scratch draft, Model A) or "Create a new workspace"
 * — with Apply gated on a choice. "Add an eval" hands off to `onAddEval` (the caller
 * routes to the workspace's `eval/new` draft view); it's disabled when the package has
 * no workspace to attach to. "Create a workspace" runs the existing `useCreateWorkspace`
 * with the name field (blank → the BE assigns a default). The modal stays open on a
 * create error for a retry, and can't be dismissed while the create is in flight.
 *
 * Reads `packageId` from page context (the create is scoped to the package); the open
 * state is owned by the caller (EvalWorkspace).
 *
 * @param {AddWorkspaceModalProps} props - open / onClose / onCreated / addEvalTarget / onAddEval.
 * @return {JSX.Element} The Add modal.
 */
export function AddWorkspaceModal({
  open,
  onClose,
  onCreated,
  addEvalTarget,
  onAddEval,
}: AddWorkspaceModalProps) {
  const { packageId } = useEval3Page();
  const {
    mutate,
    reset,
    isPending: creating,
    isError,
  } = useCreateWorkspace(packageId);
  const [choice, setChoice] = useState<AddChoice>(null);
  const [name, setName] = useState('');

  // Reset the choice + field + any prior error each time the modal opens. `reset` is a
  // stable React Query callback, so this runs only on the open toggle.
  useEffect(() => {
    if (open) {
      setChoice(null);
      setName('');
      reset();
    }
  }, [open, reset]);

  const handleClose = () => {
    if (!creating) {
      onClose();
    }
  };

  const handleSubmit = () => {
    if (creating) {
      return; // guard the Enter / double-click race (mirrors handleClose)
    }
    if (choice === 'eval' && addEvalTarget) {
      onAddEval(addEvalTarget.id);
      onClose();
      return;
    }
    if (choice === 'workspace') {
      mutate(
        { name: name.trim() || undefined },
        {
          onSuccess: (workspace) => {
            onCreated(workspace.id);
            onClose();
          },
        }
      );
    }
  };

  return (
    <Modal
      open={open}
      size="sm"
      modalHeading="Add"
      primaryButtonText={creating ? 'Creating…' : 'Apply'}
      secondaryButtonText="Cancel"
      // Gate Apply on a choice (§11g) — and on the create not being in flight.
      primaryButtonDisabled={choice === null || creating}
      onRequestSubmit={handleSubmit}
      onRequestClose={handleClose}
    >
      <RadioButtonGroup
        name="eval3-add-choice"
        legendText="What would you like to add?"
        orientation="vertical"
        valueSelected={choice ?? ''}
        // Carbon types the value as `string | number`; narrow back to a concrete choice
        // via the literal guard (no cast) so a stray radio value can't reach state.
        onChange={(value) => {
          if (value === 'eval' || value === 'workspace') {
            setChoice(value);
          }
        }}
      >
        <RadioButton
          id="eval3-add-eval"
          value="eval"
          // No workspace to attach a draft to → disabled; the label drops the target.
          labelText={
            addEvalTarget
              ? `Add an eval to ${addEvalTarget.name}`
              : 'Add an eval'
          }
          disabled={!addEvalTarget}
        />
        <RadioButton
          id="eval3-add-workspace"
          value="workspace"
          labelText="Create a new workspace"
        />
      </RadioButtonGroup>

      {/* Name field — only for the workspace path (a from-scratch eval is named later). */}
      {choice === 'workspace' && (
        <TextInput
          id="add-workspace-name"
          className={styles.nameField}
          labelText="Workspace name"
          placeholder="Untitled workspace"
          value={name}
          disabled={creating}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            setName(event.target.value)
          }
        />
      )}
      {isError && (
        <InlineNotification
          className={styles.error}
          kind="error"
          lowContrast
          hideCloseButton
          title="Couldn’t create the workspace"
          subtitle="Please try again."
        />
      )}
    </Modal>
  );
}
